/**
 * The recipe, the way you actually cook from it: the photo up top, what it takes,
 * and the steps in order. Read-only — "Edit recipe" hands off to the editor. From
 * here you can also push every ingredient onto this week's grocery list in one tap,
 * for the nights you're cooking something that isn't on the plan.
 */
import { useState } from 'react';
import type { Recipe } from '../../shared/types';
import { mondayOf, todayIso, weekLabel } from './dates';
import { errMsg, hostOf, mealsApi, qtyLabel } from './mealsApi';
import { ErrorNote, MealPhoto, Modal, btnGhost, btnPrimary, btnSecondary } from './ui';

/** Split stored instructions into display steps, dropping blanks and any leading "1." */
function steps(instructions: string | null): string[] {
  if (!instructions) return [];
  return instructions
    .split(/\r?\n/)
    .map((s) => s.replace(/^\s*\d+[.)]\s*/, '').trim())
    .filter((s) => s !== '');
}

export default function RecipeView({
  recipe,
  onEdit,
  onGroceryChanged,
  onClose,
}: {
  recipe: Recipe;
  onEdit: () => void;
  onGroceryChanged?: () => void;
  onClose: () => void;
}) {
  const ingredients = recipe.ingredients ?? [];
  // Pantry items belong to the recipe, never to the shop. "Add to grocery" has to
  // respect that or the flag means nothing the moment someone cooks from this screen.
  const toBuy = ingredients.filter((i) => !i.pantry);
  const stepList = steps(recipe.instructions);
  const week = mondayOf(todayIso());

  const [adding, setAdding] = useState(false);
  const [added, setAdded] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function addToGrocery() {
    if (toBuy.length === 0) return;
    setAdding(true);
    setError(null);
    setAdded(null);
    try {
      // Hand-added items (manual = 1) — they survive a list rebuild, so cooking this
      // tonight won't get wiped when the weekly plan regenerates the list.
      await Promise.all(
        toBuy.map((i) =>
          mealsApi.addGroceryItem({
            week_start: week,
            name: i.name,
            quantity: i.quantity,
            unit: i.unit,
            aisle_id: i.aisle_id,
          })
        )
      );
      setAdded(toBuy.length);
      onGroceryChanged?.();
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setAdding(false);
    }
  }

  return (
    <Modal
      title={recipe.name}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-end gap-2">
          <button type="button" className={btnSecondary} onClick={onClose}>
            Close
          </button>
          <button type="button" className={btnPrimary} onClick={onEdit}>
            Edit recipe
          </button>
        </div>
      }
    >
      <div className="space-y-5">
        <MealPhoto name={recipe.name} url={recipe.photo_url} className="h-48 w-full rounded-card" />

        {/* meta row */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-ink-soft">
          {recipe.servings !== null && (
            <span>
              <span aria-hidden="true">🍽 </span>
              Serves {recipe.servings}
            </span>
          )}
          {recipe.source_url && (
            <a
              href={recipe.source_url}
              target="_blank"
              rel="noreferrer"
              className="text-dusk underline underline-offset-2 hover:text-dusk/80"
            >
              Source: {hostOf(recipe.source_url)}
              <span aria-hidden="true"> ↗</span>
            </a>
          )}
        </div>

        {recipe.notes && (
          <p className="rounded-xl bg-surface-sunk p-3 text-sm text-ink-soft">{recipe.notes}</p>
        )}

        {/* ingredients */}
        <section>
          <div className="mb-2 flex items-baseline justify-between gap-2">
            <h3 className="font-display text-base font-semibold text-ink">
              Ingredients{' '}
              <span className="font-sans text-sm font-normal text-ink-faint">
                ({ingredients.length})
              </span>
            </h3>
            {toBuy.length > 0 && (
              <button
                type="button"
                className={btnGhost}
                onClick={addToGrocery}
                disabled={adding}
                aria-label={`Add ${toBuy.length} ingredients to this week's grocery list`}
              >
                {adding ? 'Adding…' : '＋ To grocery list'}
              </button>
            )}
          </div>

          {added !== null && (
            <p
              role="status"
              className="mb-2 rounded-lg bg-sage-soft px-3 py-2 text-sm text-sage"
            >
              Added {added} item{added === 1 ? '' : 's'} to the list for {weekLabel(week)}.
            </p>
          )}
          {error && (
            <div className="mb-2">
              <ErrorNote message={error} />
            </div>
          )}

          {ingredients.length === 0 ? (
            <p className="text-sm text-ink-faint">
              No ingredients listed yet. Add them from Edit and they'll feed the grocery list.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-card border border-border">
              {ingredients.map((i) => {
                const qty = qtyLabel(i.quantity, i.unit);
                return (
                  <li key={i.id} className="flex items-baseline gap-2 px-3 py-2 text-sm text-ink">
                    {qty && <span className="font-medium text-ink-soft">{qty}</span>}
                    <span>{i.name}</span>
                    {i.pantry && (
                      <span className="ml-auto shrink-0 text-xs text-ink-faint">already have</span>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* steps */}
        <section>
          <h3 className="mb-2 font-display text-base font-semibold text-ink">Instructions</h3>
          {stepList.length === 0 ? (
            <p className="text-sm text-ink-faint">
              No steps written down yet. Add them from Edit — one step per line.
            </p>
          ) : (
            <ol className="space-y-3">
              {stepList.map((step, idx) => (
                <li key={idx} className="flex gap-3">
                  <span
                    aria-hidden="true"
                    className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-sage-soft text-xs font-semibold text-sage"
                  >
                    {idx + 1}
                  </span>
                  <p className="text-sm leading-relaxed text-ink">{step}</p>
                </li>
              ))}
            </ol>
          )}
        </section>
      </div>
    </Modal>
  );
}
